import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Filter, Search, ArrowLeft, FileText, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

type AuditLogEntry = {
  id: number;
  modulo: string;
  entidadeId: number;
  userId: number;
  userName: string | null;
  acao: string;
  campo: string | null;
  valorAnterior: string | null;
  valorNovo: string | null;
  emitenteId: number | null;
  alteradoEm: Date;
};

export default function AuditLog() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [moduloFilter, setModuloFilter] = useState<string>("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Buscar módulos disponíveis para o usuário
  const { data: modulos = [] } = trpc.auditoria.modulos.useQuery(undefined, {
    enabled: !!user,
  });

  // Buscar logs de auditoria
  const { data: logsData, isLoading, refetch } = trpc.auditoria.list.useQuery(
    {
      modulo: moduloFilter !== "ALL" ? moduloFilter as any : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: 100,
    },
    { enabled: !!user }
  );

  const logs = logsData?.logs || [];

  const getModuloLabel = (modulo: string) => {
    const labels: Record<string, string> = {
      movimentacoes: "Movimentações",
      produtos: "Produtos",
      enderecos: "Endereços",
      emitentes: "Emitentes",
      usuarios: "Usuários",
    };
    return labels[modulo] || modulo;
  };

  const getModuloColor = (modulo: string) => {
    const colors: Record<string, string> = {
      movimentacoes: "text-blue-600 bg-blue-50",
      produtos: "text-green-600 bg-green-50",
      enderecos: "text-purple-600 bg-purple-50",
      emitentes: "text-orange-600 bg-orange-50",
      usuarios: "text-red-600 bg-red-50",
    };
    return colors[modulo] || "text-gray-600 bg-gray-50";
  };

  const getAcaoColor = (acao: string) => {
    switch (acao) {
      case "CREATE":
        return "text-green-600 bg-green-50";
      case "UPDATE":
        return "text-blue-600 bg-blue-50";
      case "DELETE":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getAcaoLabel = (acao: string) => {
    switch (acao) {
      case "CREATE":
        return "Criação";
      case "UPDATE":
        return "Alteração";
      case "DELETE":
        return "Exclusão";
      default:
        return acao;
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Log de Alterações</h1>
            <p className="text-gray-500 text-sm">
              Histórico de todas as alterações no sistema
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Filtro por Módulo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Módulo
            </label>
            <Select value={moduloFilter} onValueChange={setModuloFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos os módulos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos os módulos</SelectItem>
                {modulos.map((m: { value: string; label: string }) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data Início */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Início
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Data Fim */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Lista de Logs */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FileText className="h-12 w-12 mb-4 opacity-50" />
            <p>Nenhum log encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data/Hora
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Módulo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ação
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Anterior
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Novo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log: AuditLogEntry) => (
                  <tr key={`${log.modulo}-${log.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {format(new Date(log.alteradoEm), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(log.alteradoEm), { addSuffix: true, locale: ptBR })}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getModuloColor(log.modulo)}`}>
                        {getModuloLabel(log.modulo)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAcaoColor(log.acao)}`}>
                        {getAcaoLabel(log.acao)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {log.userName || `Usuário #${log.userId}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {log.campo || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500 max-w-xs truncate" title={log.valorAnterior || ""}>
                        {log.valorAnterior || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={log.valorNovo || ""}>
                        {log.valorNovo || "-"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contador de resultados */}
      {!isLoading && logs.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Exibindo {logs.length} registro(s)
        </div>
      )}
    </div>
  );
}
